import { cached, tracked } from '@glimmer/tracking';

import type { DSSetup } from 'webapp-types';
import { Unit } from 'webapp/enums/enums';
import { copyObject, uniq } from 'webapp/utils/common';

import MetricSetup from 'webapp/models-custom/metric-setup';
import type { MetricSetupBaseProps } from 'webapp/models-custom/metric-setup-base';
import MetricSetupBase from 'webapp/models-custom/metric-setup-base';

export interface MetricForMasterFilter {
	dimensions: DSSetup.DimensionItem[];
	autoselectedDimension: null;
	allowDimensions: true;
	mustSelectDimension: false;
	hasUnits: false;
	isGeneric: false;
}

export default class MetricSetupForMasterFilter extends MetricSetupBase {
	@tracked _metricDimensions: DSSetup.DimensionItem[] = [];
	@tracked _metricData: DSSetup.MetricData[] = [];

	// this flag is true when we want to show empty dimension input (not NONE)
	// e.g. on opening board master filter popup where mixed dimensions are selected
	@tracked isUnspecificDimensionSelected = true;

	constructor(
		props: MetricSetupBaseProps = {},
		metricDimensions: DSSetup.DimensionItem[] = [],
		metricData: DSSetup.MetricData[] = [],
		isUnspecificDimensionSelected = true,
	) {
		super(props);

		// set before afterMetricSet reads them: writing them after a read in the same render fails autotracking
		this._metricDimensions = metricDimensions;
		this._metricData = metricData;
		this.isUnspecificDimensionSelected = isUnspecificDimensionSelected;
		this.afterMetricSet(props);
	}

	@cached
	get metric(): MetricForMasterFilter {
		return {
			dimensions: this.metricDimensions,
			autoselectedDimension: null,
			allowDimensions: true,
			mustSelectDimension: false,
			isGeneric: false,
			hasUnits: false,
		};
	}

	get defaultUnit(): string {
		return Unit.ALL;
	}

	override get dimensions() {
		return super.dimensions;
	}

	override set dimensions(value: string[]) {
		super.dimensions = value;
		// write without reading first: a read-then-write in the same render fails autotracking
		this.isUnspecificDimensionSelected = false;
	}

	override get metricDimensions() {
		return this._metricDimensions || [];
	}

	@cached
	get sourceTypeKeys() {
		// for tracking
		const keys: string[] = [];
		this._metricData.forEach((metricData) => {
			const metricSetup = new MetricSetup(metricData),
				sourceTypeKey = metricSetup.metric?.dataSourceTypeKey;
			if (sourceTypeKey) {
				keys.push(sourceTypeKey);
			}
		});
		return uniq(keys);
	}

	@cached
	override get metricSetupDataForFetchDimensionValues() {
		return this.getMetricSetupDataForFetchDimensionValues(this.dimensions);
	}

	override getDimensionNameForDimension(dimension: string) {
		return this.metricDimensions.find((d) => d.key === dimension)?.value || dimension;
	}

	createCopy() {
		const masterFilter = new MetricSetupForMasterFilter(
			{
				dimensions: this.dimensions,
				filters: this.filtersData,
			},
			copyObject(this._metricDimensions),
			copyObject(this._metricData),
			this.isUnspecificDimensionSelected,
		);
		return masterFilter;
	}

	private getMetricSetupDataForFetchDimensionValues(dimensions: string[]) {
		if (!dimensions.length) {
			return [];
		}

		return this._metricData.map((metricData) => {
			return {
				key: metricData.key,
				spaceAccessId: metricData.spaceAccessId,
				dimensions: dimensions,
			};
		});
	}
}
